import React from "react";

const API_BASE_URL = process.env.REACT_APP_PATH_TO_API;

export interface AuthResponse {
    token: string;
    expiresAt: number; // seconds since epoch
    sub: string;
    userAttributes: {
        firstName: string;
        lastName: string;
        email: string;
        picture: string;
        uploadCount: string;
        uploadLimit: string;
        downloadCount: string;
        downloadLimit: string;
        enableFaceAnalysis?: boolean;
    enableImageAnalysis?: boolean;
    }
}

export interface AuthResult {
    success: boolean;
    error?: string; // todo: this should be redefined so that there is a list of possible error code values rather than just an error string
}

export interface TokenCheckResult {
    isValid: boolean;
    wasRefreshed: boolean;
    error?: string;
}

export const checkAndRefreshTokenIfNeeded = async (): Promise<TokenCheckResult> => {
    try {
        const expiresAtString = sessionStorage.getItem('expiresAt');
        const expiresAt = expiresAtString ? Number(expiresAtString) : null;
        const currentTime = Math.floor(Date.now() / 1000);

        // If expiresAt is missing or invalid, assume a new tab and attempt a refresh
        if (!expiresAt || isNaN(expiresAt) || expiresAt <= currentTime) {
            console.log('No valid token or token expired. Attempting to refresh.');
            const refreshResult = await refreshToken();

            if (refreshResult.success) {
                return {
                    isValid: true,
                    wasRefreshed: true
                };
            } else {
                return {
                    isValid: false,
                    wasRefreshed: false,
                    error: refreshResult.error
                };
            }
        }

        // Check if token is close to expiry (e.g., < 5 mins)
        const secondsUntilExpiry = expiresAt - currentTime;
        if (secondsUntilExpiry > 300) {
            return {
                isValid: true,
                wasRefreshed: false
            };
        }

        // Token is expiring soon, attempt refresh
        console.log('Token expires in less than 5 minutes. Refreshing!');
        const refreshResult = await refreshToken();

        if (refreshResult.success) {
            return {
                isValid: true,
                wasRefreshed: true
            };
        } else {
            return {
                isValid: false,
                wasRefreshed: false,
                error: refreshResult.error
            };
        }
    } catch (error) {
        console.error('Token check/refresh error:', error);
        return {
            isValid: false,
            wasRefreshed: false,
            error: 'Failed to check/refresh token'
        };
    }
};

// Hook for using token refresh in components
export const useTokenRefresh = () => {
    const checkAndRefreshTokens = React.useCallback(async (): Promise<boolean> => {
        const result = await checkAndRefreshTokenIfNeeded();
        return result.isValid;
    }, []);

    return checkAndRefreshTokens;
};

// Helper function to store auth session data
const storeAuthSession = (authResponse: AuthResponse) => {
    console.log(authResponse);
    localStorage.setItem('sub', authResponse.sub);
    sessionStorage.setItem('token', authResponse.token);
    sessionStorage.setItem('expiresAt', authResponse.expiresAt.toString());

    const { userAttributes } = authResponse;
    sessionStorage.setItem('email', userAttributes.email);
    sessionStorage.setItem('firstName', userAttributes.firstName);
    sessionStorage.setItem('lastName', userAttributes.lastName);
    sessionStorage.setItem('profileUrl', userAttributes.picture);
    sessionStorage.setItem('uploadCount', userAttributes.uploadCount);
    sessionStorage.setItem('uploadLimit', userAttributes.uploadLimit);
    sessionStorage.setItem('downloadCount', userAttributes.downloadCount);
    sessionStorage.setItem('downloadLimit', userAttributes.downloadLimit);
    
    // Store analysis settings if they exist in the response
    // These might come as separate properties or as part of a nested object
    if (authResponse.userAttributes.enableFaceAnalysis !== undefined) {
        sessionStorage.setItem('enableFaceAnalysis', authResponse.userAttributes.enableFaceAnalysis.toString());
    }
    if (authResponse.userAttributes.enableImageAnalysis !== undefined) {
        sessionStorage.setItem('enableImageAnalysis', authResponse.userAttributes.enableImageAnalysis.toString());
    }
};

// Process API response and store session data
const processSocialAuthResponse = async (response: Response): Promise<AuthResult> => {
    if (!response.ok) {
        const errorData = await response.json();
        return {
            success: false,
            error: errorData.message || 'Authentication failed. Please try again.'
        };
    }

    try {
        const data = await response.json();
        console.log(data);

        // Parse the JSON string in data.body
        const authData = JSON.parse(data.body);

        // Now pass the parsed object to storeAuthSession
        storeAuthSession(authData);

        return { success: true };
    } catch (error) {
        console.error("Error processing response data:", error);
        return {
            success: false,
            error: 'Error processing authentication response'
        };
    }
};

/// Process API response and store session data
const processAuthResponse = async (response: Response): Promise<AuthResult> => {
    if (!response.ok) {
        const errorData = await response.json();
        return {
            success: false,
            error: errorData.message || 'Authentication failed. Please try again.'
        };
    }

    try {
        const data = await response.json();
        storeAuthSession(data);

        return { success: true };
    } catch (error) {
        console.error("Error processing response data:", error);
        return {
            success: false,
            error: 'Error processing authentication response'
        };
    }
};

// Handle OAuth callback
export const handleAuthCallback = async (code: string): Promise<AuthResult> => {
    try {
        const response = await fetch(`${API_BASE_URL}/auth/token-exchange`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ code }),
        });

        return await processSocialAuthResponse(response);
    } catch (error) {
        console.error('Auth callback error:', error);
        return {
            success: false,
            error: 'Authentication failed. Please try again.'
        };
    }
};

// Handle direct sign in
export const signIn = async (email: string, password: string): Promise<AuthResult> => {
    try {
        const response = await fetch(`${API_BASE_URL}/auth/signin`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ action: 'signin', email: email, password: password }),
        });
        console.log(response);
        let data = await response.json();
        console.log(data);

        if (!response.ok) {
            return {
                success: false,
                error: data.error.code || 'Authentication failed. Please try again.'
            };
        }

        try {
            storeAuthSession(data);
            return { success: true };
        } catch (error) {
            console.error("Error processing response data:", error);
            return {
                success: false,
                error: 'Error processing authentication response'
            };
        }

    } catch (error) {
        console.error('Sign in error:', error);
        return {
            success: false,
            error: 'Authentication failed. Please try again.'
        };
    }
};

// Refresh token
export const refreshToken = async (): Promise<AuthResult> => {
    console.log('Hey! from refreshing token...');
    try {
        const response = await fetch(`${API_BASE_URL}/auth/refresh-token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
        });

        return await processAuthResponse(response);
    } catch (error) {
        console.error('Token refresh error:', error);
        return {
            success: false,
            error: 'Failed to refresh authentication token'
        };
    }
};

// Logout
export const logout = async (): Promise<void> => {
    try {
        await fetch(`${API_BASE_URL}/auth/sign-out`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
        });

        // Clear all auth-related storage
        localStorage.removeItem('sub');
        sessionStorage.clear();
    } catch (error) {
        console.error('Logout error:', error);
    }
};

// Define the interface for the signup response
interface SignUpResponse {
    success: boolean;
    message?: string;
    error?: string;
    errorCode?: string;
    sub?: string;
    isVerified: boolean;
    codeDeliveryDetails?: {
        AttributeName: string;
        DeliveryMedium: string;
        Destination: string;
    };
}

/**
 * Handle user sign-up process
 * @returns A promise that resolves to a SignUpResponse object
 */
export async function handleSignUp(
    email: string,
    password: string,
    firstName: string,
    lastName: string
): Promise<SignUpResponse> {
    try {
        console.log(`Sending sign-up request to ${API_BASE_URL}`);

        const response = await fetch(`${API_BASE_URL}/auth/signup`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include', // allows the cookie to be set on successful sign in
            body: JSON.stringify({ email, password, firstName, lastName }),
        });

        const data = await response.json();
        console.log('Sign-up response:', data);

        // Create a standard response format
        const result: SignUpResponse = {
            success: data.success || response.ok,
            isVerified: data.isVerified || false,
            ...data // spread the rest of the data fields
        };

        if (!result.success) {
            // If there was an error but no specific error message was provided
            if (!result.error) {
                result.error = 'Sign-up failed for an unknown reason.';
            }
            console.error('Sign-up error:', result.error, result.errorCode);
        } else {
            // Track TOS agreement if sign-up was successful
            if (result.sub) {
                trackTOSAgreement(result.sub);
            }
        }
        return result;

    } catch (error) {
        console.error('Sign-up exception:', error);
        // Return a standardized error response
        return {
            success: false,
            error: error instanceof Error ? error.message : 'An unexpected error occurred during sign-up.',
            isVerified: false
        };
    }
}

// Define interface for verification response
export interface VerificationResponse {
    success: boolean;
    error?: string;
    errorCode?: string;
    message?: string;
}

export async function verifyEmailWithCode(
    sub: string,
    verificationCode: string,
    // onCodeError: (codeWrong: boolean) => void
): Promise<VerificationResponse> {
    try {
        console.log(`creating email verification request with ${sub}, ${verificationCode}`);
        console.log(`Sending verification request to ${API_BASE_URL}`);
        
        const response = await fetch(`${API_BASE_URL}/auth/verify-email`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            // credentials: 'include',
            body: JSON.stringify({
                action: 'confirm',
                username: sub,
                code: verificationCode
            }),
        });

        console.log(response);
        const data = await response.json();
        console.log(data);
        if (response.ok) {
            return { success: true, message: 'Email verified successfully' };
        } else {
            console.log(response.status);
            return data.error;
        }

    } catch (error) {
        console.error('Verification error:', error);
        // onCodeError(true);
        return {
            success: false,
            error: 'An unexpected error occurred during verification. Please try again.',
            errorCode: 'UnexpectedError'
        };
    }
}

export interface ResendCodeResponse {
    success: boolean;
    error?: string;
    message?: string;
}

export async function resendVerificationCode(sub: string): Promise<ResendCodeResponse> {
    try {
        const response = await fetch(`${API_BASE_URL}/auth/verify-email`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ action: 'resend', username: sub }),
        });

        const data = await response.json();
        console.log(data);
    
        if (response.ok) {
            return {
                success: true,
                message: 'Verification code has been resent successfully'
            };
        } else {
            console.log(data.error);
            return {
                success: false,
                error: data.error || 'Failed to resend verification code'
            };
        }
    } catch (error) {
        console.error('Resend code error:', error);
        return {
            success: false,
            error: 'An unexpected error occurred while resending the code'
        };
    }
}

export async function handlePasswordChangeSubmit(
    session: string,
    username: string,
    firstName: string,
    lastName: string,
    newPassword: string,
    onLoginSuccess: () => void,
    // onSuccessfulSignIn: (authResults: AuthenticationResultType, userResults: any) => void
) {
    try {
        const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({session, username, firstName, lastName, newPassword}),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Password change failed.');
        }

        const data = await response.json();
        console.log(data);

        if (data.authResults) {
            onLoginSuccess();
            // onSuccessfulSignIn(data.authResults, data.userResults);
        }
    } catch (error) {
        console.error('Password change error:', error);
        throw error;
    }
}

// todo: resolve this endpoint?
// Function to track TOS agreement in DynamoDB via API Gateway
const trackTOSAgreement = async (sub: string) => {
    const agreementData = {
        userId: sub,  // Use Cognito 'sub' as the user ID
        tosAgreementTimestamp: new Date().toISOString(),
        tosVersion: '1.0',  // Optional: Use the actual TOS version
    };

    try {
        const response = await fetch('https://mg27jllmfg.execute-api.us-west-2.amazonaws.com/production/user/tos', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(agreementData),
        });

        if (!response.ok) {
            throw new Error('Failed to track TOS agreement');
        }
        console.log('TOS agreement tracked successfully');
    } catch (error) {
        console.error('Error saving TOS agreement:', error);
    }
};

export interface PasswordResetResponse {
    success: boolean;
    error?: string;
    message?: string;
}

export async function requestPasswordReset(email: string): Promise<PasswordResetResponse> {
    try {
        const response = await fetch(`${API_BASE_URL}/auth/signin`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ 
                action: 'forgotPassword', 
                email: email 
            }),
        });
        console.log(response);
        const data = await response.json();
        console.log(data);
        if (response.ok) {
            console.log('password reset request successfully');
            return {
                success: true,
                message: 'A verification code has been sent to your email'
            };
        } else {
            console.log('password reset request failed');
            return {
                success: false,
                error: data.error || 'Failed to request password reset'
            };
        }
    } catch (error) {
        console.error('Password reset request error:', error);
        return {
            success: false,
            error: 'An unexpected error occurred'
        };
    }
}

export async function verifyCodeAndResetPassword(
    email: string,
    code: string,
    newPassword: string
): Promise<PasswordResetResponse> {
    try {
        const response = await fetch(`${API_BASE_URL}/auth/signin`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
                action: 'confirmForgotPassword',
                email: email,
                code: code,
                newPassword: newPassword
            }),
        });

        const data = await response.json();
        console.log(data);

        if (response.ok) {
            return {
                success: true,
                message: 'Password has been reset successfully'
            };
        } else {
            return {
                success: false,
                error: data.error || 'Failed to reset password'
            };
        }
    } catch (error) {
        console.error('Password reset error:', error);
        return {
            success: false,
            error: 'An unexpected error occurred'
        };
    }
}

export async function resetPassword(
    token: string,
    newPassword: string,
    isForced: boolean = false
): Promise<PasswordResetResponse> {
    try {
        const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(isForced && { 'Authorization': `Bearer ${sessionStorage.getItem('token')}` })
            },
            body: JSON.stringify({
                token: isForced ? undefined : token,
                newPassword
            }),
        });

        const data = await response.json();
        
        if (response.ok) {
            return {
                success: true,
                message: 'Password has been reset successfully'
            };
        } else {
            return {
                success: false,
                error: data.error || 'Failed to reset password'
            };
        }
    } catch (error) {
        console.error('Password reset error:', error);
        return {
            success: false,
            error: 'An unexpected error occurred'
        };
    }
}